import React from 'react'

const formatMoney = (value) => (Number.isFinite(Number(value)) ? `CZK ${Math.round(Number(value)).toLocaleString('en-US')}` : '—')
const formatPercent = (value) => (Number.isFinite(Number(value)) ? `${Number(value).toFixed(1)} %` : '—')

const ResultRow = ({ label, value, strong = false }) => (
  <div className="flex items-center justify-between gap-4 border-b border-slate-200 py-3 text-sm last:border-b-0">
    <span className="text-slate-500">{label}</span>
    <span className={strong ? 'text-right font-semibold text-slate-900' : 'text-right font-medium text-slate-800'}>{value}</span>
  </div>
)

const FeasibilityCalculationResult = ({ preview, profitSharePercent = 100, embedded = false, showVatAndWarnings = true }) => {
  if (!preview) return null
  const result = preview.result || {}
  const taxResult = preview.taxResult || {}
  const targetPricePerM2 = result.targetSalePricePerM2 ?? result.weightedSalePricePerM2
  const totalTax = taxResult.totalTaxAndContributions
  const taxCost = Number.isFinite(Number(totalTax)) ? Math.abs(Number(totalTax)) : null
  const projectNetProfit =
    taxResult.projectNetProfit ??
    (Number.isFinite(Number(result.profitBeforeTax)) && taxCost !== null ? Number(result.profitBeforeTax) - taxCost : null)
  const userNetProfit = taxResult.userNetProfit ?? projectNetProfit

  return (
    <section className={embedded ? 'overflow-hidden border-y border-slate-200 bg-white' : 'overflow-hidden rounded-xl border border-emerald-200 bg-white shadow-sm'}>
      {!embedded && (
        <div className="border-b border-slate-200 px-5 py-4">
          <h3 className="text-lg font-semibold text-slate-900">Výsledek kalkulace</h3>
        </div>
      )}
      <div className="bg-emerald-50 px-4 py-4 sm:px-5 sm:py-5">
        <div className="text-sm text-emerald-950/70">Můj čistý podíl {Number(profitSharePercent) !== 100 ? `(${profitSharePercent} %)` : ''}</div>
        <div className={`${embedded ? 'text-2xl' : 'text-3xl'} mt-1 font-bold tracking-tight text-emerald-800`}>{formatMoney(userNetProfit)}</div>
      </div>
      <div className="px-4 py-2 sm:px-5 sm:py-3">
        <ResultRow label="Prodejní obytná plocha" value={Number.isFinite(Number(result.saleableAreaM2)) ? `${result.saleableAreaM2} m²` : '—'} />
        <ResultRow label="Cílová prodejní cena / m²" value={formatMoney(targetPricePerM2)} />
        <ResultRow label="Tržby" value={formatMoney(result.revenue)} />
        <ResultRow label="Stavba / rekonstrukce" value={formatMoney(result.renovationCost)} />
        <ResultRow label="Celkové náklady" value={formatMoney(result.totalCost)} strong />
        <ResultRow label="Bod zvratu / m²" value={formatMoney(result.breakEvenPricePerM2)} />
        <ResultRow label="Zisk před zdaněním" value={formatMoney(result.profitBeforeTax)} />
        <ResultRow label="ROI před zdaněním" value={formatPercent(result.roiBeforeTaxPercent)} strong />
        {taxCost !== null && <ResultRow label="Daně a odvody celkem" value={`-${formatMoney(taxCost)}`} strong />}
        {Number.isFinite(Number(projectNetProfit)) && <ResultRow label="Čistý zisk projektu" value={formatMoney(projectNetProfit)} strong />}
        <ResultRow label="ROI po zdanění" value={formatPercent(taxResult.afterTaxRoiPercent)} strong />
        <ResultRow label="Můj čistý podíl" value={formatMoney(userNetProfit)} strong />
      </div>
      {showVatAndWarnings && preview.vat?.message && (
        <p className="border-t border-amber-200 bg-amber-50 px-5 py-3 text-xs text-amber-900">{preview.vat.message}</p>
      )}
      {showVatAndWarnings && preview.warnings?.length > 0 && (
        <div className="border-t border-amber-200 bg-amber-50 px-5 py-3 text-xs text-amber-900">
          {preview.warnings.map((warning, index) => <p key={`${warning}:${index}`}>{warning}</p>)}
        </div>
      )}
    </section>
  )
}

export default FeasibilityCalculationResult
